import { compatibility, useGetUrl } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { Button, Inline, Stack, Text, TextLink } from 'braid-design-system';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import {
  useCreateSharedProfileUrl,
  useGetSharedProfileVisibility,
} from 'src/modules/graphql/visibility/data';
import { useTypedSelector } from 'src/reducers';

import translations from './.vocab';
import { SharedProfileLink } from './SharedProfileLink';
import { SharedProfileVisibility } from './SharedProfileVisibility';

export const EditSharedProfileSettings = () => {
  const { t } = useTranslations(translations);
  const { language: melwaysLanguage } = useMelwaysInfo();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(melwaysLanguage);
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const getUrl = useGetUrl(zone, language, true);
  const { trackSharedProfileLinkCreated } = useAnalytics();

  const { shareableProfileUrl } = useGetSharedProfileVisibility();

  const { createShareableProfileUrl } = useCreateSharedProfileUrl();

  const createLinkOnClick = () => {
    createShareableProfileUrl({
      variables: {
        languageCode: language,
        zone,
      },
    });

    trackSharedProfileLinkCreated();

    metrics.count('shareable_profile_generated');
  };

  return shareableProfileUrl ? (
    <Stack space="large">
      <Text tone="secondary">
        {t('You can share this link to...', {
          TextLink: (children) => (
            <TextLink
              weight="weak"
              hitArea="large"
              target="_blank"
              href={getUrl('shareable-profile-faq-url')}
            >
              {children}
            </TextLink>
          ),
        })}
      </Text>
      <SharedProfileLink />
      <SharedProfileVisibility />
    </Stack>
  ) : (
    <Stack space="large">
      <Text tone="secondary">
        {t('Once you create this link, others can view...', {
          TextLink: (children) => (
            <TextLink
              weight="weak"
              hitArea="large"
              target="_blank"
              href={getUrl('shareable-profile-faq-url')}
            >
              {children}
            </TextLink>
          ),
        })}
      </Text>
      <Inline space="small" collapseBelow="tablet">
        <Button tone="brandAccent" onClick={createLinkOnClick}>
          Consent and create link
        </Button>
      </Inline>
    </Stack>
  );
};
